import { useCallback } from 'react';
import {
  useDashboardDistricts,
  useDashboardTriggers,
  useDashboardVerticals,
  useDashboardStatus,
  useDashboardStartDate,
  useDashboardEndDate,
  useDashboardCostState,
  useDashboardKeywordsAndExclusions,
  useBrokerData,
  useDocumentUrl,
  useRealEstateData,
  useDashboardZoneExclusions,
  useDashboardExcludePastProjectStage,
  useDashboardPastProjectStage,
  useDashboardVerticalsExclusions,
  useDashboardCompaniesState,
  useProjectSize,
  useParcelSize,
  useWorkTypes,
  useWorkTypesExclusions,
  useDashboardCompanyKeywordsAndExclusions,
  useCreatedAtStart,
  useCreatedAtEnd,
  useCityIds,
  useDashboardCompanyStakeholderTypes,
  useDashboardCompanyStakeholderTypesExclusions,
  useUserCountySelection,
} from 'state/dashboard';
import { useLatestProjectStageFilterState } from 'state/filterToolbar';
import { DEFAULT_VERTICAL_EXCLUSIONS, getDefaultEndDate } from 'utils/constants';

export const useClearToolbarFilterState = () => {
  const { resetLatestProjectStageFilterState } = useLatestProjectStageFilterState();

  const clearToolbarFilterState = useCallback(() => {
    resetLatestProjectStageFilterState();
  }, [resetLatestProjectStageFilterState]);

  return { clearToolbarFilterState };
};

/**
 * Clears all city specific filters on filterToolbar
 */
export const useClearRegionSpecificFilters = () => {
  const [, saveTriggers] = useDashboardTriggers();
  const [, setDistrictsGroup] = useDashboardDistricts();
  const [, setZoneExclusions] = useDashboardZoneExclusions();
  const [, setRealEstate] = useRealEstateData();
  const [, setBrokerData] = useBrokerData();
  const [, setDocumentUrl] = useDocumentUrl();
  const [, setCityIds] = useCityIds();
  const [, setPastProjectStages] = useDashboardPastProjectStage();
  const [, setExcludePastProjectStages] = useDashboardExcludePastProjectStage();
  const { clearToolbarFilterState } = useClearToolbarFilterState();
  const [, setCountyCodes] = useUserCountySelection();

  const clearRegionSpecificFilters = useCallback(() => {
    setCityIds([]);
    setCountyCodes([]);
    saveTriggers([]);
    setPastProjectStages([]);
    setExcludePastProjectStages([]);
    setDistrictsGroup([]);
    setZoneExclusions([]);
    setRealEstate(false);
    setBrokerData(false);
    setDocumentUrl(false);
    clearToolbarFilterState();
  }, [
    saveTriggers,
    setDistrictsGroup,
    setZoneExclusions,
    setRealEstate,
    setBrokerData,
    setDocumentUrl,
    setCityIds,
    setCountyCodes,
  ]);

  return { clearRegionSpecificFilters };
};

/**
 * Indiscriminately clears all filters on filterToolbar
 */
export const useClearAllFilters = () => {
  const defaultEndDate = getDefaultEndDate();
  const [, setDistrictsGroup] = useDashboardDistricts();
  const [, setZoneExclusions] = useDashboardZoneExclusions();
  const [, setVerticals] = useDashboardVerticals();
  const [, setVerticalExclusions] = useDashboardVerticalsExclusions();
  const [, saveTriggers] = useDashboardTriggers();
  const { updateKeywords: setGlobalKeywords, updateExclusions: setGlobalExclusions } =
    useDashboardKeywordsAndExclusions();
  const {
    updateKeywords: setGlobalCompanyKeywords,
    updateExclusions: setGlobalCompanyExclusions,
  } = useDashboardCompanyKeywordsAndExclusions();
  const [, setSavedCost] = useDashboardCostState();
  const [, setStatus] = useDashboardStatus();
  const [, setUpdateAtStartDate] = useDashboardStartDate();
  const [, setUpdatedAtEndDate] = useDashboardEndDate();
  const [, setCreatedAtStartDate] = useCreatedAtStart();
  const [, setCreatedAtEndDate] = useCreatedAtEnd();
  const [, setRealEstate] = useRealEstateData();
  const [, setBrokerData] = useBrokerData();
  const [, setDocumentUrl] = useDocumentUrl();
  const [, setPastProjectStages] = useDashboardPastProjectStage();
  const [, setExcludePastProjectStages] = useDashboardExcludePastProjectStage();
  const [, setCompanies] = useDashboardCompaniesState();
  const { setProjectSizeMin, setProjectSizeMax, setProjectSizeIncludeUnknown } =
    useProjectSize();
  const { setParcelSizeMin, setParcelSizeMax, setParcelSizeIncludeUnknown } =
    useParcelSize();
  const [, setWorkTypes] = useWorkTypes();
  const [, setWorkTypesExclusions] = useWorkTypesExclusions();
  const [, setCityIds] = useCityIds();
  const [, setCompanyStakeholderTypes] = useDashboardCompanyStakeholderTypes();
  const [, setCompanyStakeholderTypesExclusions] =
    useDashboardCompanyStakeholderTypesExclusions();
  const { clearToolbarFilterState } = useClearToolbarFilterState();
  const [, setCountyCodes] = useUserCountySelection();

  const clearAllFilters = useCallback(() => {
    saveTriggers([]);
    setDistrictsGroup([]);
    setVerticals([]);
    setCityIds([]);
    setSavedCost({ min: 0, max: 0 });
    setUpdateAtStartDate('anytime');
    setUpdatedAtEndDate(defaultEndDate);
    setCreatedAtStartDate('anytime');
    setCreatedAtEndDate(defaultEndDate);
    setStatus('active');
    setGlobalKeywords('');
    setGlobalCompanyKeywords('');
    setRealEstate(false);
    setBrokerData(false);
    setDocumentUrl(false);
    setGlobalExclusions('');
    setGlobalCompanyExclusions('');
    setZoneExclusions([]);
    setPastProjectStages([]);
    setExcludePastProjectStages([]);
    setVerticalExclusions(DEFAULT_VERTICAL_EXCLUSIONS);
    setCompanies([]);
    setProjectSizeMin(null);
    setProjectSizeMax(null);
    setProjectSizeIncludeUnknown(true);
    setParcelSizeMin(null);
    setParcelSizeMax(null);
    setParcelSizeIncludeUnknown(true);
    setWorkTypes([]);
    setWorkTypesExclusions([]);
    setCompanyStakeholderTypes('');
    setCompanyStakeholderTypesExclusions('');
    clearToolbarFilterState();
    setCountyCodes([]);
  }, [
    saveTriggers,
    setDistrictsGroup,
    setVerticals,
    setSavedCost,
    setUpdateAtStartDate,
    setUpdatedAtEndDate,
    setCreatedAtStartDate,
    setCreatedAtEndDate,
    setStatus,
    setGlobalKeywords,
    setGlobalCompanyKeywords,
    setRealEstate,
    setBrokerData,
    setDocumentUrl,
    setGlobalExclusions,
    setGlobalCompanyExclusions,
    setZoneExclusions,
    setPastProjectStages,
    setExcludePastProjectStages,
    setVerticalExclusions,
    setCompanies,
    setProjectSizeMin,
    setProjectSizeMax,
    setProjectSizeIncludeUnknown,
    setParcelSizeMin,
    setParcelSizeMax,
    setParcelSizeIncludeUnknown,
    setWorkTypes,
    setWorkTypesExclusions,
    setCityIds,
    setCompanyStakeholderTypes,
    setCompanyStakeholderTypesExclusions,
    clearToolbarFilterState,
    setCountyCodes,
  ]);

  return { clearAllFilters };
};
